import { sleep } from "@/utils/helpers"
import {
  Commitment,
  Connection,
  GetBalanceConfig,
  GetParsedProgramAccountsConfig,
  PublicKey,
  SolanaJSONRPCError,
} from "@solana/web3.js"
import type { Agent as NodeHttpAgent } from "http"
import { Agent as NodeHttpsAgent } from "https"
import RpcClient from "jayson/lib/client/browser"
import fetch from "node-fetch"
import type { Struct } from "superstruct"
import {
  any,
  array,
  boolean,
  coerce,
  create,
  instance,
  literal,
  number,
  optional,
  type as pick,
  string,
  tuple,
  union,
  unknown,
} from "superstruct"

type RpcRequest = (methodName: string, args: Array<any>) => Promise<any>

const COMMON_HTTP_HEADERS = {
  "solana-client": `js/${process.env.npm_package_version ?? "UNKNOWN"}`,
}

function createRpcClient(url: string): RpcClient {
  let agent: NodeHttpAgent | NodeHttpsAgent | undefined
  const clientBrowser = new RpcClient(async (request, callback) => {
    const options = {
      method: "POST",
      body: request,
      agent,
      headers: Object.assign(
        {
          "Content-Type": "application/json",
        },
        {},
        COMMON_HTTP_HEADERS,
      ),
    }

    try {
      let too_many_requests_retries = 5
      let res
      let waitTime = 500
      for (;;) {
        res = await fetch(url, options)
        if (res.status !== 429 /* Too many requests */) {
          break
        }
        too_many_requests_retries -= 1
        if (too_many_requests_retries === 0) {
          break
        }
        console.log(`Server responded with ${res.status} ${res.statusText}.  Retrying after ${waitTime}ms delay...`)
        await sleep(waitTime)
        waitTime *= 2
      }

      const text = await res.text()
      if (res.ok) {
        callback(null, text)
      } else {
        callback(new Error(`${res.status} ${res.statusText}: ${text}`))
      }
    } catch (err) {
      if (err instanceof Error) callback(err)
    }
  }, {})

  return clientBrowser
}

function createRpcRequest(client: RpcClient): RpcRequest {
  return (method, args) => {
    return new Promise((resolve, reject) => {
      client.request(method, args, (err: any, response: any) => {
        if (err) {
          reject(err)
          return
        }
        resolve(response)
      })
    })
  }
}

function extractCommitmentFromConfig<TConfig>(
  commitmentOrConfig?: Commitment | ({ commitment?: Commitment } & TConfig),
) {
  let commitment: Commitment | undefined
  let config: Omit<TConfig, "commitment"> | undefined
  if (typeof commitmentOrConfig === "string") {
    commitment = commitmentOrConfig
  } else if (commitmentOrConfig) {
    const { commitment: specifiedCommitment, ...specifiedConfig } = commitmentOrConfig
    commitment = specifiedCommitment
    config = specifiedConfig
  }
  return { commitment, config }
}

const UnknownRpcResult = createRpcResult(unknown())

const PublicKeyFromString = coerce(instance(PublicKey), string(), (value) => new PublicKey(value))

const RawAccountDataResult = tuple([string(), literal("base64")])

const BufferFromRawAccountData = coerce(instance(Buffer), RawAccountDataResult, (value) =>
  Buffer.from(value[0], "base64"),
)

const ParsedAccountDataResult = pick({
  program: string(),
  parsed: unknown(),
  space: number(),
})

const ParsedOrRawAccountData = coerce(
  union([instance(Buffer), ParsedAccountDataResult]),
  union([RawAccountDataResult, ParsedAccountDataResult]),
  (value) => {
    if (Array.isArray(value)) {
      return create(value, BufferFromRawAccountData)
    } else {
      return value
    }
  },
)

const ParsedAccountInfoResult = pick({
  executable: boolean(),
  owner: PublicKeyFromString,
  lamports: number(),
  data: ParsedOrRawAccountData,
  rentEpoch: number(),
})

const KeyedParsedAccountInfoResult = pick({
  pubkey: PublicKeyFromString,
  account: ParsedAccountInfoResult,
})

function createRpcResult<T, U>(result: Struct<T, U>) {
  return union([
    pick({
      jsonrpc: literal("2.0"),
      id: string(),
      result,
    }),
    pick({
      jsonrpc: literal("2.0"),
      id: string(),
      error: pick({
        code: unknown(),
        message: string(),
        data: optional(any()),
      }),
    }),
  ])
}

function jsonRpcResult<T, U>(schema: Struct<T, U>) {
  return coerce(createRpcResult(schema), UnknownRpcResult, (value) => {
    if ("error" in value) {
      return value
    } else {
      return {
        ...value,
        result: create(value.result, schema),
      }
    }
  })
}

/**
 * @internal
 */
function jsonRpcResultAndContext<T, U>(value: Struct<T, U>) {
  return jsonRpcResult(
    pick({
      context: pick({
        slot: number(),
      }),
      value,
    }),
  )
}

export class CustomConnection {
  /** @internal */ _rpcClient: RpcClient
  /** @internal */ _rpcRequest: RpcRequest
  /** @internal */ _connection: Connection

  constructor(connection: Connection) {
    this._connection = connection
    this._rpcClient = createRpcClient(connection.rpcEndpoint)
    this._rpcRequest = createRpcRequest(this._rpcClient)
  }

  async getParsedProgramAccountsWithAbortSignal(
    programId: PublicKey,
    configOrCommitment?: GetParsedProgramAccountsConfig | Commitment,
    abortController?: AbortController,
  ) {
    const abortSignal = abortController?.signal
    if (abortSignal && abortSignal.aborted) {
      return Promise.reject(abortSignal.reason)
    }
    const { commitment, config } = extractCommitmentFromConfig(configOrCommitment)
    const args = this._connection._buildArgs([programId.toBase58()], commitment, "jsonParsed", config)
    const unsafeRes = this._rpcRequest("getProgramAccounts", args)
    const cancellationPromise = this.getCancellationPromise(abortSignal)
    const outcome = await Promise.race([unsafeRes, cancellationPromise])
    const res = create(outcome, jsonRpcResult(array(KeyedParsedAccountInfoResult)))
    if ("error" in res) {
      throw new SolanaJSONRPCError(res.error, `failed to get accounts owned by program ${programId.toBase58()}`)
    }
    return res.result
  }

  /**
   * Fetch the balance for the specified public key
   */
  async getBalanceWithAbortSignal(
    publicKey: PublicKey,
    commitmentOrConfig?: Commitment | GetBalanceConfig,
    abortController?: AbortController,
  ): Promise<number> {
    const abortSignal = abortController?.signal
    if (abortSignal && abortSignal.aborted) {
      return Promise.reject(abortSignal.reason)
    }
    const { commitment, config } = extractCommitmentFromConfig(commitmentOrConfig)
    const args = this._connection._buildArgs([publicKey.toBase58()], commitment, undefined /* encoding */, config)
    const unsafeRes = this._rpcRequest("getBalance", args)
    const cancellationPromise = this.getCancellationPromise(abortSignal)
    const outcome = await Promise.race([unsafeRes, cancellationPromise])
    const res = create(outcome, jsonRpcResultAndContext(number()))
    if ("error" in res) {
      throw new SolanaJSONRPCError(res.error, `failed to get balance for ${publicKey.toBase58()}`)
    }
    return res.result.value
  }

  private getCancellationPromise(signal?: AbortSignal): Promise<never> {
    return new Promise<never>((_, reject) => {
      if (signal == null) {
        return
      }
      if (signal.aborted) {
        reject(signal.reason)
      } else {
        signal.addEventListener("abort", () => {
          reject(signal.reason)
        })
      }
    })
  }
}
